import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router'
import { Button, Form, Input } from 'antd'
import React from 'react'
import { PortaledBreadcrumb } from '../components/portaled-breadcrumb'
import { useTranslation } from 'react-i18next'
import { useStoreUser } from '../user/hooks'
import { CREATE_USER } from '../user/permission'
import { FormItem } from '../components/form-item'
import useForm from '../hooks/use-form'
import { LocaleRadio } from '../user/components/locale-radio'
import { RoleRadio } from '../user/components/role-radio'
import { CUSTOMER } from '../user/role'
import { StoreInput } from '../user/input/store-input'
import { useAuth } from '../auth'
import { useTitle } from '../hooks/use-title'
import { PageTitle } from '../components/page-title'

export const Route = createFileRoute('/_auth/_main/users/create')({
    beforeLoad: ({ context }) => {
        if (!context.auth.can(CREATE_USER)) {
            throw redirect({
                to: '/dashboard',
            })
        }
    },
    component: CreateUser,
})

function CreateUser() {
    const { t } = useTranslation()
    const mutation = useStoreUser()
    const { user } = useAuth()
    const navigate = useNavigate()

    const formProps = useForm({
        mutation,
        onSuccess(user) {
            navigate({
                to: `/users/${user.id}`,
            })
        },
    })

    const role = Form.useWatch('role', formProps.form)

    useTitle(t('Create {{name}}', { name: t('User') }))

    return (
        <>
            <PortaledBreadcrumb
                items={[
                    {
                        title: <Link to="/users">{t('Users')}</Link>,
                    },
                    {
                        title: t('Add'),
                    },
                ]}
            />
            <PageTitle
                title={t('Add {{name}}', { name: t('User') })}
                level={4}
            />
            <Form
                layout="vertical"
                className="max-w-96"
                initialValues={{
                    locale: user?.locale,
                }}
                {...formProps}
            >
                <FormItem<StoreInput>
                    label={t('Role')}
                    name="role"
                    required
                >
                    <RoleRadio />
                </FormItem>

                <FormItem<StoreInput>
                    label={t('Name')}
                    name="name"
                    required
                >
                    <Input
                        name="name"
                        placeholder="John Doe"
                    />
                </FormItem>

                <FormItem<StoreInput>
                    label={t('Email')}
                    name="email"
                    required
                >
                    <Input
                        type="email"
                        name="email"
                        placeholder="john@example.com"
                    />
                </FormItem>
                {role === CUSTOMER && (
                    <FormItem<StoreInput>
                        label={t('Company name')}
                        name="companyName"
                    >
                        <Input placeholder="Evaa B.V." />
                    </FormItem>
                )}
                <FormItem<StoreInput>
                    label={t('Language')}
                    name="locale"
                    required
                >
                    <LocaleRadio />
                </FormItem>
                <Button
                    loading={mutation.isPending}
                    size="large"
                    type="primary"
                    htmlType="submit"
                >
                    {t('Add')}
                </Button>
            </Form>
        </>
    )
}
